import React, { useEffect, useState } from "react";
import { X, Upload } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
  fetchCategory,
  fetchCountry,
  fetchMealPriority,
  fetchMealDetail,
  addMealimg,
} from "../../redux/slices/gallerySlice";

const AddMealModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [isUploadimg, setIsUploadImg] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const { categoryList, countryList, priorityList, loading } = useSelector(
    (state) => state.gallery
  );

  const [form, setForm] = useState({
    category_master_id: "",
    country_id: "",
    name: "",
    priority: "",
    image: null,
  });

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCategory());
      dispatch(fetchCountry());
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      setForm({ ...form, [name]: files[0] });
      setImagePreview(URL.createObjectURL(files[0]));
      setIsUploadImg(true);
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;

    setForm({
      ...form,
      category_master_id: value,
      priority: "",
    });

    dispatch(fetchMealPriority(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalForm = {
      ...form,
      priority: form.priority === "new" ? "" : form.priority,
    };

    if (
      !finalForm.category_master_id ||
      !finalForm.country_id ||
      !finalForm.name ||
      !finalForm.image
    ) {
      Swal.fire(
        "Validation Error",
        "Please fill all fields & select an image.",
        "error"
      );
      return;
    }

    setIsAdding(true); // START SPINNER

    try {
      const res = await dispatch(addMealimg(finalForm)).unwrap();

      if (res.success) {
        Swal.fire("Success", "Image added successfully!", "success");

        dispatch(fetchMealDetail(1));
        onClose();

        // Reset form
        setForm({
          category_master_id: "",
          country_id: "",
          name: "",
          priority: "",
          image: null,
        });
        setImagePreview(null);
        setIsUploadImg(false);
      } else {
        Swal.fire("Error", res.message || "Something went wrong", "error");
      }
    } catch (err) {
      Swal.fire("Error", err?.message || "Something went wrong", "error");
    } finally {
      setImagePreview(null);
      setIsAdding(false); // STOP SPINNER
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"></div>

      <div className="fixed inset-0 flex justify-center items-center z-50 px-4">
        <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg">
          {/* Header */}
          <div className="flex justify-between items-center border-b px-6 py-4">
            <h2 className="text-xl font-semibold">Add Meal Image</h2>
            <button onClick={onClose}>
              <X size={22} />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Country */}
            <select
              name="country_id"
              value={form.country_id}
              onChange={handleChange}
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select country</option>

              {countryList?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.country_name}
                </option>
              ))}
            </select>

            {/* Category */}
            <select
              name="category_master_id"
              value={form.category_master_id}
              onChange={handleCategoryChange}
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select category</option>

              {categoryList?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            {/* Priority */}
            {/* <select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select priority</option>

              {priorityList?.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select> */}

            {/* Priority */}
            <select
              name="priority"
              value={form.priority}
              onChange={handleChange}
              className="p-3 border rounded-md w-full"
            >
              <option value="">Select priority</option>

              {priorityList && priorityList.length > 0 ? (
                priorityList.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))
              ) : (
                <option value="new">New</option>
              )}
            </select>

            {/* Name */}
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter image name"
              className="p-3 border rounded-md w-full"
            />

            {/* Image Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center flex flex-col justify-center items-center hover:border-orange-400 transition relative">
              <Upload size={40} className="text-gray-400 mb-3" />

              <input
                type="file"
                name="image"
                id="image"
                onChange={handleChange}
                className="hidden"
              />

              {isUploadimg ? (
                <label
                  className="text-orange-500 cursor-pointer"
                  htmlFor="image"
                >
                  Click to Change
                </label>
              ) : (
                <label
                  className="text-orange-500 cursor-pointer"
                  htmlFor="image"
                >
                  Click to Upload
                </label>
              )}

              {form.image && (
                <p className="mt-2 text-orange-500 text-bold text-xs">
                  {form.image.name}
                </p>
              )}
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="preview"
                  className="w-24 h-24 object-cover mx-auto mt-2 rounded"
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t px-6 py-4">
            <button onClick={onClose} className="px-5 py-2 border rounded-md">
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={isAdding}
              className="px-6 py-2 rounded-md bg-orange-500 text-white flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isAdding ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMealModal;
